import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Package } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="grid min-h-[60vh] place-items-center text-ink/50">Loading…</div>;
  }

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-ivory px-5 py-16 md:px-10 md:py-24" data-testid="account-page">
      <div className="mx-auto max-w-[1100px]">
        <Reveal className="flex flex-col justify-between gap-6 border-b border-black/5 pb-10 md:flex-row md:items-end">
          <div>
            <p className="eyebrow text-gold">My Account</p>
            <h1 className="mt-3 font-serif text-5xl font-light tracking-tight text-ink md:text-6xl">
              Hello, {user.name?.split(" ")[0]}
            </h1>
            <p className="mt-2 text-sm text-ink/60">{user.email}</p>
          </div>
          <button
            onClick={doLogout}
            className="flex w-fit items-center gap-2 rounded-full border border-ink px-6 py-3 text-[0.7rem] uppercase tracking-[0.22em] text-ink transition-colors hover:bg-ink hover:text-ivory"
            data-testid="logout-button"
          >
            <LogOut size={15} strokeWidth={1.5} /> Log Out
          </button>
        </Reveal>

        <div className="mt-12">
          <h2 className="font-serif text-3xl text-ink">Order History</h2>
          {orders.length === 0 ? (
            <div className="mt-8 flex flex-col items-center gap-5 rounded-2xl bg-cream py-16 text-center">
              <Package size={40} strokeWidth={1} className="text-sand" />
              <p className="text-ink/60">You haven't placed any orders yet.</p>
              <Link to="/shop" className="rounded-full bg-ink px-8 py-3.5 text-[0.7rem] uppercase tracking-[0.22em] text-ivory transition-colors hover:bg-gold">
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="mt-8 space-y-5" data-testid="orders-list">
              {orders.map((o) => (
                <div key={o.id} className="rounded-2xl border border-black/5 bg-cream p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-serif text-xl text-ink">{o.order_number}</p>
                      <p className="text-xs text-ink/50">{new Date(o.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="rounded-full bg-gold/15 px-4 py-1.5 text-[0.62rem] uppercase tracking-[0.18em] text-gold">
                      {o.status}
                    </span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {o.items.map((it) => (
                      <img key={it.slug} src={it.image} alt={it.name} className="h-16 w-14 rounded-lg object-cover" />
                    ))}
                  </div>
                  <p className="mt-4 text-sm text-ink/70">Total: <span className="text-ink">${o.total.toFixed(2)}</span></p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
